import {
  type SavableProperty,
  type SavablePropertyRule,
  savablePropertyRequestWrapper,
  useSavableProperty,
} from "#src/composables/savable-property.composable.ts";
import { QuestionsService } from "#src/services/http-questions.service.ts";
import { generateUuid } from "#src/util/helpers.ts";
import { type Pinia } from "pinia";
import { mdiCalendar, mdiPencil } from "@mdi/js";
import {
  CheckboxField,
  CurrencyField,
  DateField,
  type FormFieldProps,
  type BuilderProps,
  FormFieldAdapter,
  RadioField,
  SelectField,
  TextField,
} from "#src/components/form-builder/FormBuilderTemplates.ts";
import {
  validateDate,
  validateExact,
  validateInList,
  validateNumber,
  validateText,
} from "#src/composables/savable-property-validators.ts";

export const UNDERWRITING_QUESTION_TYPES = {
  CHECKBOX: "checkbox",
  SELECT: "select",
  DATE: "date",
  TEXT: "text",
  RADIO: "radio",
  INTEGER: "integer",
};

type UnderwritingQuestionType =
  (typeof UNDERWRITING_QUESTION_TYPES)[keyof typeof UNDERWRITING_QUESTION_TYPES];

const UNDERWRITING_RADIO_OPTIONS = [
  { title: "Yes", value: "1" },
  { title: "No", value: "0" },
];

export function isNotDefined<T>(val: T) {
  return val === null || val === undefined;
}

const mapTextToTitle = (
  options: UnderwritingQuestionRawSelectItem[]
): UnderwritingQuestionSelectItem[] =>
  options.map(v => ({
    title: v.text,
    value: v.value,
  }));

interface UnderwritingQuestionRawSelectItem {
  text: string;
  value: number | string | null;
}
interface UnderwritingQuestionSelectItem {
  title: string;
  value: number | string | null;
}

export interface UnderwritingQuestion<T = any> {
  id: number;
  questionId: string; // Used to reference when stored
  parentId: string | null;
  label: string | null;
  inputLabel: string | null;
  requiredParentValue: number | string | null;
  requiredValue: number | string | null;
  type: UnderwritingQuestionType;
  answer: SavableProperty<T>;
  items?: UnderwritingQuestionSelectItem[];
}

export interface RawUnderwritingQuestion {
  id: number;
  field_type: UnderwritingQuestionType;
  verbatim_question: string;
  required_parent_value: number | string | null;
  required_value: number | string | null;
  answer: {
    id: number;
    answer: number | string | null;
  };
  field_label: string | null;
  answer2: string[] | number[];
  field_options: UnderwritingQuestionRawSelectItem[];
  insured_index: number | null;
  children: RawUnderwritingQuestion[];
}

function baseQuestionData(
  raw: RawUnderwritingQuestion,
  options: { parentId: string | null }
) {
  return {
    id: raw.id,
    questionId: `underwriting-question-${raw.id}`,
    parentId: options.parentId,
    label: raw.verbatim_question,
    requiredParentValue: raw.required_parent_value,
    requiredValue: raw.required_value,
    type: raw.field_type,
    inputLabel: null,
  };
}

function initializeCheckboxType(
  raw: RawUnderwritingQuestion,
  options: { parentId: string | null }
) {
  const items = mapTextToTitle(raw.field_options);

  const question: UnderwritingQuestion<string[] | number[] | boolean[]> = {
    ...baseQuestionData(raw, options),
    items,
    answer: useSavableProperty<string[] | number[] | boolean[]>({
      requestMap: "answer",
      group: "root",
      rules: {
        inListValidator: {
          v(this) {
            if (!Array.isArray(this.model)) return false;
            if (!this.model?.length) return false;
            return this.model.every(modelAnswer =>
              items.some(v => v.value === modelAnswer)
            );
          },
          message: "Required",
        },
      },
    }),
  };

  if (question.requiredValue) {
    question.answer.rules.isRequiredValue = validateExact(
      question.requiredValue
    );
  }

  question.answer.model = [];
  const answer = raw.answer2;
  if (Array.isArray(answer) && answer.length) {
    question.answer.load(raw.answer2 || []);
    question.answer.validate(true);
  }

  return question;
}

function initializeSelectType(
  raw: RawUnderwritingQuestion,
  options: { parentId: string | null }
): UnderwritingQuestion<string | number | boolean> {
  const items = mapTextToTitle(raw.field_options);
  const question: UnderwritingQuestion<string | number | boolean> = {
    ...baseQuestionData(raw, options),
    inputLabel: raw.field_label || "Please select",
    items,
    answer: useSavableProperty<string | number | boolean>({
      requestMap: "answer",
      group: "root",
      rules: {
        inListValidator: validateInList(
          items.map((i: any) => i.value),
          "Required"
        ),
      },
    }),
  };

  if (question.requiredValue) {
    question.answer.rules.isRequiredValue = validateExact(
      question.requiredValue
    );
  }

  const answer = raw.answer.answer;
  if (!isNotDefined(answer)) {
    question.answer.load(answer as string | number | boolean);
    question.answer.validate(true);
  }

  return question;
}

function initializeRadioType(
  raw: RawUnderwritingQuestion,
  options: { parentId: string | null }
): UnderwritingQuestion<string | number | boolean> {
  const items = UNDERWRITING_RADIO_OPTIONS;
  const question: UnderwritingQuestion<string | number | boolean> = {
    ...baseQuestionData(raw, options),
    inputLabel: null,
    items,
    answer: useSavableProperty<string | number | boolean>({
      requestMap: "answer",
      group: "root",
      rules: {
        inListValidator: validateInList(
          items.map((i: any) => i.value),
          "Required"
        ),
      },
    }),
  };

  if (question.requiredValue) {
    question.answer.rules.isRequiredValue = validateExact(
      question.requiredValue
    );
  }

  const answer = raw.answer.answer;
  if (!isNotDefined(answer)) {
    question.answer.load(answer as string | number | boolean);
    question.answer.validate(true);
  }

  return question;
}

function initializeIntegerType(
  raw: RawUnderwritingQuestion,
  options: { parentId: string | null }
): UnderwritingQuestion<number> {
  const question: UnderwritingQuestion<number> = {
    ...baseQuestionData(raw, options),
    inputLabel: raw.field_label || "Enter a Number",
    answer: useSavableProperty<number>({
      requestMap: "answer",
      group: "root",
      rules: {
        isValid: validateNumber(),
      },
    }),
  };

  if (question.requiredValue) {
    question.answer.rules.isRequiredValue = validateExact(
      question.requiredValue
    );
  }

  const answer = raw.answer.answer;
  if (!isNotDefined(answer)) {
    question.answer.load(+(answer as number));
    question.answer.validate(true);
  }

  return question;
}

function initializeTextType(
  raw: RawUnderwritingQuestion,
  options: { parentId: string | null }
): UnderwritingQuestion<string> {
  const question: UnderwritingQuestion<string> = {
    ...baseQuestionData(raw, options),
    inputLabel: raw.field_label || "Please explain",
    answer: useSavableProperty<string>({
      requestMap: "answer",
      group: "root",
      rules: {
        isValid: validateText({ minLength: 1 }),
      },
    }),
  };
  if (question.requiredValue) {
    question.answer.rules.isRequiredValue = validateExact(
      question.requiredValue
    );
  }

  const answer = raw.answer.answer;
  if (!isNotDefined(answer)) {
    question.answer.load(answer as string);
    question.answer.validate(true);
  }

  return question;
}

function initializeDateType(
  raw: RawUnderwritingQuestion,
  options: { parentId: string | null }
): UnderwritingQuestion<string> {
  const question: UnderwritingQuestion<string> = {
    ...baseQuestionData(raw, options),
    inputLabel: raw.field_label || "Please explain",
    answer: useSavableProperty<string>({
      requestMap: "answer",
      group: "root",
      rules: {
        isValid: validateDate(),
      },
    }),
  };

  if (question.requiredValue) {
    question.answer.rules.isRequiredValue = validateExact(
      question.requiredValue
    );
  }

  const answer = raw.answer.answer;
  if (!isNotDefined(answer)) {
    question.answer.load(answer as string);
    question.answer.validate(true);
  }

  return question;
}

export function initializeUnderwritingQuestion(
  raw: RawUnderwritingQuestion,
  options: { parentId: string | null }
): UnderwritingQuestion {
  return {
    [UNDERWRITING_QUESTION_TYPES.CHECKBOX]: initializeCheckboxType,
    [UNDERWRITING_QUESTION_TYPES.DATE]: initializeDateType,
    [UNDERWRITING_QUESTION_TYPES.TEXT]: initializeTextType,
    [UNDERWRITING_QUESTION_TYPES.RADIO]: initializeRadioType,
    [UNDERWRITING_QUESTION_TYPES.SELECT]: initializeSelectType,
    [UNDERWRITING_QUESTION_TYPES.INTEGER]: initializeIntegerType,
  }[raw.field_type](raw, options);
}

export async function saveAttributes<T = any>(
  underwritingQuestion: UnderwritingQuestion<T>,
  pinia: Pinia
) {
  const questionsService = new QuestionsService(pinia);
  const body = { answer: underwritingQuestion.answer.format() };
  savablePropertyRequestWrapper(
    () =>
      questionsService.updateUnderwritingQuestion(
        underwritingQuestion.id,
        body
      ),
    {
      composableAttributes: [underwritingQuestion.answer],
      body,
      uuid: generateUuid(),
    }
  );
}

function getFieldConfig<T = any>(
  question: UnderwritingQuestion<T>,
  options: {
    dataTestid: string;
    savableProperty: SavableProperty<T>;
    executeSaveHook: boolean;
  }
) {
  switch (question.type) {
    case UNDERWRITING_QUESTION_TYPES.INTEGER: {
      return CurrencyField({
        ...options,
        includeNegatives: false,
        includeDecimals: false,
      });
    }
    case UNDERWRITING_QUESTION_TYPES.SELECT: {
      if (!question.items) throw "Missing items";
      return SelectField({
        ...options,
        items: question.items,
      });
    }
    case UNDERWRITING_QUESTION_TYPES.TEXT: {
      return TextField({
        ...options,
        prependInnerIcon: mdiPencil,
      });
    }
    case UNDERWRITING_QUESTION_TYPES.CHECKBOX: {
      if (!question.items) throw "Missing items";
      return CheckboxField({
        ...options,
        items: question.items,
      });
    }
    case UNDERWRITING_QUESTION_TYPES.RADIO: {
      if (!question.items) throw "Missing items";
      return RadioField({
        ...options,
        items: question.items,
        inline: true,
        centered: true,
      });
    }
    case UNDERWRITING_QUESTION_TYPES.DATE: {
      return DateField({
        ...options,
        placeholder: "mm/dd/yyyy",
        prependInnerIcon: mdiCalendar,
      });
    }
    default:
      throw "Missing Underwriting Question Type";
  }
}

export function useUnderwritingQuestionSavableProperty<T = any>(
  pinia: Pinia,
  question: UnderwritingQuestion<T>,
  additionalProps: Partial<
    { componentProps?: Partial<FormFieldProps> } & Partial<BuilderProps>
  > = {}
) {
  const fieldConfig = getFieldConfig(question, {
    dataTestid: `${question.questionId}-${question.type}`,
    savableProperty: question.answer,
    executeSaveHook: true,
  });
  if (!fieldConfig) throw `Undefined field config for type ${question.type}`;

  const { componentProps, ...builderProps } = additionalProps;

  if (
    [
      UNDERWRITING_QUESTION_TYPES.DATE,
      UNDERWRITING_QUESTION_TYPES.TEXT,
      UNDERWRITING_QUESTION_TYPES.SELECT,
    ].includes(question.type)
  ) {
    builderProps.class = builderProps.class || "pt-1";
  }

  return FormFieldAdapter(
    fieldConfig,
    {
      key: `${question.questionId}-${question.type}`,
      ...builderProps,
    },
    {
      ...componentProps,
      save: () => saveAttributes(question, pinia),
    }
  );
}
